import * as React from 'react'
import produce from 'immer'
import { Base64 } from 'js-base64'
import * as CSV from 'csv-string'
import { Button, Form, Input, Spin, Dropdown, Menu, message } from 'antd'
import { cloneDeep, get, isNil, set, isEqual, isEmpty, remove, flow } from 'lodash'
import { ValidationError, showError, showClientNotifications } from 'helpers/errors'
import {
  createFieldFactory,
  createHandleSubmit,
  createLabelFactory,
  getChangedItems,
  isReadOnly,
} from 'helpers/formViews'
import { formatUserTime } from 'helpers/dateTime'
import { t } from 'helpers/i18n'
import { withRouter } from 'react-router-dom'
import { encodeState } from 'helpers/utils'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_SUBMIT_EVENT, FORM_CHANGE_EVENT, LIST_MODAL_CLEAR_SELECTION } from 'options/events'
import FormButtons from 'elements/FormButtons'
import Modal from 'elements/Modal'
import Select, { Option, getOptionProps } from 'elements/Select'
import Icon from 'elements/Icon'
import CycleCountItems from 'containers/CycleCounts/Items'
import CreateCycleCount from 'containers/CycleCounts/Create'
import Split from 'containers/CycleCounts/FormView/Split'
import SelectInventory from 'containers/Inventory/Select'
import SelectListItems from 'containers/Lists/Items/Select'
import Drawer from 'elements/Drawer'
import FileUpload from 'elements/FileUpload'
import SelectItems from 'containers/CycleCounts/Items/Select'
import Print from 'containers/CycleCounts/Print'
import { Row, Col } from 'elements/Grid'
import { formatValue } from 'helpers/listViews'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.printableRef = React.createRef()
    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  handleFormChange = () => this.setState({ formHasChanged: true })

  handleCreateCycleCount = async (saved) => {
    await this.promiseState({ item: saved, createCycleCountVisible: false })
    await this.fetchItem()
    await this.props.onSave?.()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.cycleCountId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    if (isNil(itemId) && !this.props.linkTargetRecord) {
      this.setState({ createCycleCountVisible: true })
    } else {
      try {
        const responses = await Promise.all([
          this.props.getItem(itemId).catch((error) => {
            this.props.onCancel?.()
            throw error
          }),
          this.props.getSettings({ type: this.props.settingsType }),
          this.props.getDetailItems(itemId),
          this.props.getUsers(),
        ])

        const item = cloneDeep(responses[0].value.data)

        this.setState({
          item,
          itemOriginal: cloneDeep(item),
          fieldSettings: get(responses[1], 'value.data.fieldSettings', []),
          detailItems: get(responses[2], 'value.data.items', []),
          detailItemsOriginal: get(responses[2], 'value.data.items', []),
          detailItemsTotals: get(responses[2], 'value.data.pageTotals', []),
          users: get(responses[3], 'value.data.items', []),
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.detailItems, this.state.detailItemsOriginal)

  handleCommitButtonClick = () => {
    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    Modal.confirm({
      autoFocusButton: 'ok',
      title: t('confirmCommitTitle'),
      content: t('confirmCommitDescription'),
      okType: 'primary',
      okText: t('commit'),
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          const { id: cycleCountId = 0, sendDifferenceReportTo } = this.state.item

          await this.props.onSave?.(true)
          this.setState({ commitButtonLoading: true })

          await this.props
            .commitCycleCount({ id: cycleCountId })
            .then((response) => showClientNotifications({ response }))

          if (!isEmpty(sendDifferenceReportTo)) {
            const cycleCount = await this.props.getItem(cycleCountId).then((r) => r.value.data)

            const reportItems = await this.props
              .getCycleCountDifferenceReportItems({ cycleCountId, pageIndex: 0 })
              .then((r) => r.value.data.items)

            await this.props
              .emailCycleCountDifferenceReportItems({
                ...cycleCount,
                customerName: this.props.customer.name,
                reportItems,
              })
              .then((response) => showClientNotifications({ response }))
          }

          await this.fetchItem()

          await this.props.onSave?.(false)
        } catch (error) {
          showError({ error })
          await this.props.onSave?.(false)
        } finally {
          this.setState({ commitButtonLoading: false })
        }
      },
    })
  }

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'clearAllItemCounts':
        Modal.confirm({
          autoFocusButton: 'ok',
          title: t('confirmClearCountValues'),
          okType: 'danger',
          okText: t('clear'),
          cancelText: t('cancel'),
          onOk: () =>
            this.setState(
              produce((draft) => {
                draft.detailItems.forEach((one) => {
                  one.count = -1
                  one.count2 = -1
                })
              })
            ),
        })
        break

      case 'deleteAllUncountedItemsOnCount':
        Modal.confirm({
          autoFocusButton: 'ok',
          title: t('confirmDeleteUncountedItems'),
          okType: 'danger',
          okText: t('delete'),
          cancelText: t('cancel'),
          onOk: () =>
            this.setState(
              produce((draft) => {
                remove(draft.detailItems, (each) => each.count === -1 && each.count2 === -1)
              })
            ),
        })
        break

      case 'differenceReport':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.props.history.push(
            `/reports/inventory/cycleCountDifferenceReport?${encodeState({
              showFilter: true,
              filterDto: {
                cycleCountId: this.state.item.id,
              },
            })}`
          )
          this.props.onCancel()
        }
        break

      case 'splitCycleCount':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ splitCycleCountVisible: true })
        }
        break

      case 'print':
        this.setState(
          produce((draft) => {
            draft.printItem = { ...draft.item, ...this.props.form.getFieldsValue() }
            draft.printVisible = true
          })
        )
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = await this.props.updateItem(item)

    await this.saveCycleCountDetails(saved.value.data.id)

    return saved
  }

  saveCycleCountDetails = async (cycleCountId) => {
    const { creating, updating, deleting } = getChangedItems(this.state.detailItemsOriginal, this.state.detailItems)

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteDetailItems(
        cycleCountId,
        deleting.map((each) => each.id)
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    if (!isEmpty(updating)) {
      const response = await this.props.updateDetailItems(
        cycleCountId,
        updating.map((each) => ({ ...each, cycleCountId }))
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    if (!isEmpty(creating)) {
      const response = await this.props.createDetailItems(
        cycleCountId,
        creating.map(({ id, jobLabourId, ...rest }) => ({
          ...rest,
          cycleCountId,
        }))
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }
  }

  handleItemsTableExtraButtonsClick = ({ key }) => {
    switch (key) {
      case 'addFromInventory':
        this.setState({ addFromInventoryVisible: true, addFromInventoryItems: [] })
        break

      case 'addFromList':
        this.setState({
          addFromListVisible: true,
          addFromListItems: [],
          selectPopulatedItems: [],
          selectPopulatedItemsData: [],
        })
        break

      case 'addFromFile':
        this.setState({
          addFromFileUploadVisible: true,
          selectPopulatedItems: [],
          selectPopulatedItemsData: [],
        })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleAddFromInventorySubmit = async () => {
    try {
      const seed = Date.now() * -1

      this.setState({ addFromInventoryLoading: true })

      const response = await this.props.populateCycleCountItems(
        this.state.item.id,
        this.state.addFromInventoryItems.map((each) => each.barcode)
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      this.setState(
        produce((draft) => {
          get(response, 'value.data.items', [])
            .filter((each) => each.inventoryId && !each.lockedForCycleCount)
            .forEach((each, index) => {
              if (isNil(draft.detailItems.find((one) => one.barcode === each.barcode))) {
                draft.detailItems.push({ ...each, id: seed - index, cycleCountId: draft.item.id })
              }
            })

          draft.addFromInventoryItems = []
          draft.addFromInventoryVisible = false
        })
      )

      Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ addFromInventoryLoading: false })
    }
  }

  handleAddFromListSubmit = async () => {
    try {
      const seed = Date.now() * -1

      this.setState({ addFromListLoading: true })

      const response = await this.props.populateCycleCountItems(
        this.state.item.id,
        this.state.addFromListItems.map((each) => each.barcode)
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      const populatedItems = get(response, 'value.data.items', []).map((each, index) => ({
        ...each,
        id: each.id > 0 ? seed + index : (seed + index) * -1,
        cycleCountId: this.state.item.id,
      }))

      this.setState({
        selectPopulatedItemsData: populatedItems,
        selectPopulatedItemsVisible: true,
        selectPopulatedItemsTitle: t('addFromList'),
      })

      Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
    } catch (error) {
      showError({ error })

      this.setState({
        selectPopulatedItemsVisible: false,
        selectPopulatedItems: [],
      })
    } finally {
      this.setState({ addFromListLoading: false })
    }
  }

  handleAddFromFileUpload = async (fileList = []) => {
    try {
      const barcodes = CSV.parse(Base64.decode(fileList[0].fileContents)).map((parts) => parts[0])

      const response = await this.props.populateCycleCountItems(this.state.item.id, barcodes)

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      const seed = Date.now()

      const populatedItems = get(response, 'value.data.items', []).map((each, index) => ({
        ...each,
        id: each.id > 0 ? seed + index : (seed + index) * -1,
        cycleCountId: this.state.item.id,
      }))

      this.setState({
        addFromFileUploadVisible: false,
        selectPopulatedItemsData: populatedItems,
        selectPopulatedItemsVisible: true,
        selectPopulatedItemsTitle: t('addFromFile'),
      })
    } catch (error) {
      showError({ error })

      this.setState({
        selectPopulatedItemsVisible: false,
        selectPopulatedItems: [],
      })
    }
  }

  handleSelectPopulatedItemsSubmit = () => {
    let seed = Date.now() * -1
    let count = 0

    this.setState(
      produce((draft) => {
        draft.selectPopulatedItems
          .filter((each) => each.inventoryId && !each.lockedForCycleCount)
          .forEach((each) => {
            if (isNil(draft.detailItems.find((one) => one.barcode === each.barcode))) {
              draft.detailItems.push({ ...each, id: seed--, cycleCountId: draft.item.id })
              count++
            }
          })
      }),
      () => message.success(`${count} ${t('itemsAdded')}`)
    )
  }

  render() {
    const { item, fieldSettings, validationErrors, detailItems } = this.state

    if (!item || !fieldSettings) {
      return (
        <>
          <Spin />
          <Modal
            title={t('createCycleCount')}
            visible={this.state.createCycleCountVisible}
            onCancel={this.props.onCancel}
            okText={t('create')}
            onOk={() => Emitter.emit(FORM_SUBMIT_EVENT)}
            okButtonProps={{ loading: this.state.createCycleCountLoading }}
          >
            <CreateCycleCount
              onSave={(pending) => this.setState({ createCycleCountLoading: pending })}
              onSaveAndClose={this.handleCreateCycleCount}
              onCancel={this.props.onCancel}
            />
          </Modal>
        </>
      )
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || item.status === 'Committed'

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <Dropdown
            overlay={
              <Menu onClick={this.handleActionsMenuClick}>
                {item.status !== 'Committed' && [
                  <Menu.Item key="splitCycleCount" disabled={readOnly || isEmpty(detailItems)}>
                    {t('splitCycleCount...')}
                  </Menu.Item>,
                  <Menu.Divider key="div1" />,
                  <Menu.Item key="clearAllItemCounts" disabled={readOnly || isEmpty(detailItems)}>
                    {t('clearAllItemCounts')}
                  </Menu.Item>,
                  <Menu.Item key="deleteAllUncountedItemsOnCount" disabled={readOnly || isEmpty(detailItems)}>
                    {t('deleteAllUncountedItemsOnCount')}
                  </Menu.Item>,
                  <Menu.Divider key="div2" />,
                ]}
                <Menu.Item key="differenceReport">{t('differenceReport')}</Menu.Item>
                <Menu.Divider />
                <Menu.Item key="print">{t('print...')}</Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
          <div className="form-items-container">
            <ValidationError errors={validationErrors} />
            <Row>
              <Col xs={24} md={6}>
                <Form.Item label={createLabel('locationName')}>
                  <Input value={item.locationName} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={item.type !== 'Custom' ? 4 : 6}>
                <Form.Item label={createLabel('itemsToCount')}>
                  <Input value={item.itemsToCount} disabled />
                </Form.Item>
              </Col>
              {item.type !== 'Custom' && (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('itemsAtLocation')}>
                    <Input value={item.itemsAtLocation} disabled />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={item.type !== 'Custom' ? 4 : 6}>
                <Form.Item label={createLabel('itemsCounted')}>
                  <Input value={item.itemsCounted} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label={t('created')}>
                  <Input value={formatUserTime(item.createdDate, item.createdBy)} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={3}>
                <Form.Item label={createLabel('typeDescription')}>
                  <Input value={item.typeDescription} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={3}>
                <Form.Item label={createLabel('statusDescription')}>
                  <Input value={item.statusDescription} disabled />
                </Form.Item>
              </Col>
              {item.type === 'RangeOfBarcodes' ? (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('firstBarcode')}>
                    <Input value={item.firstBarcode} disabled />
                  </Form.Item>
                </Col>
              ) : item.type === 'RangeOfBinLocations' ? (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('firstBinLocation')}>
                    <Input value={item.firstBinLocation} disabled />
                  </Form.Item>
                </Col>
              ) : item.type !== 'Custom' ? (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('elapsedDays')}>
                    <Input value={item.elapsedDays} disabled />
                  </Form.Item>
                </Col>
              ) : null}
              <Col xs={24} md={item.type !== 'Custom' ? 4 : 6}>
                <Form.Item label={createLabel('itemsOnCount')}>
                  <Input value={item.itemsOnCount} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={item.type !== 'Custom' ? 4 : 6}>
                <Form.Item label={createLabel('itemsAdjusted')}>
                  <Input value={item.itemsAdjusted} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label={t('committed')}>
                  <Input value={formatUserTime(item.committedDate, item.committedBy)} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={6}>
                <Form.Item label={createLabel('sendDifferenceReportTo')}>
                  {createFieldDecorator('sendDifferenceReportTo')(
                    <Select
                      onChange={(value) => this.setItemValue('sendDifferenceReportTo', value)}
                      placeholder={t('none')}
                      readOnly={readOnly}
                      disabled={this.state.item.status === 'Committed'}
                    >
                      <Option value="">{t('none')}</Option>
                      {(this.state.users ?? []).map((each) => (
                        <Option key={each.userName} value={each.userName}>
                          <span {...getOptionProps(each)}>{each.displayName}</span>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              {item.type === 'Price' && (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('basePrice')}>
                    <Input value={item.basePrice} disabled />
                  </Form.Item>
                </Col>
              )}
              {item.type === 'RangeOfBarcodes' && (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('lastBarcode')}>
                    <Input value={item.lastBarcode} disabled />
                  </Form.Item>
                </Col>
              )}
              {item.type === 'RangeOfBinLocations' && (
                <Col xs={24} md={4}>
                  <Form.Item label={createLabel('lastBinLocation')}>
                    <Input value={item.lastBinLocation} disabled />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={4}>
                <Form.Item label={createLabel('inventoryAccuracy')}>
                  <Input
                    value={formatValue({
                      value: item.inventoryAccuracy,
                      displayFormat: fieldSettings.find((one) => one.dtoFieldName === 'inventoryAccuracy')
                        .displayFormat,
                    })}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <CycleCountItems
              parentRecord={item}
              items={this.state.detailItems ?? []}
              originals={this.state.detailItemsOriginal ?? []}
              pageTotals={this.state.detailItemsTotals ?? {}}
              onChange={(values) => this.setState({ detailItems: values })}
              readOnly={readOnly}
              onTableExtraButtonsClick={this.handleItemsTableExtraButtonsClick}
            />
          </div>
          <div className="form-buttons-container">
            <div className="float-right text-right">
              <FormButtons
                readOnly={readOnly}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
              />
            </div>
            {!readOnly && item.status !== 'Committed' && (
              <div className="float-left text-left">
                <Button
                  type="primary"
                  onClick={this.handleCommitButtonClick}
                  loading={this.state.commitButtonLoading}
                  disabled={isEmpty(detailItems)}
                >
                  {t('commit')}
                </Button>
              </div>
            )}
          </div>
        </Form>
        <Modal
          title={t('splitCycleCount')}
          visible={this.state.splitCycleCountVisible}
          onCancel={() => this.setState({ splitCycleCountVisible: false })}
          okText={t('splitCount')}
          onOk={() => Emitter.emit(FORM_SUBMIT_EVENT)}
          okButtonProps={{ loading: this.state.splitCycleCountLoading }}
        >
          <Split
            parentRecord={item}
            onCancel={() => this.setState({ splitCycleCountVisible: false })}
            onSave={(pending) =>
              this.promiseState({ splitCycleCountLoading: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
          />
        </Modal>
        <Modal
          title={t('addFromInventory')}
          visible={this.state.addFromInventoryVisible}
          onCancel={() => this.setState({ addFromInventoryVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ addFromInventoryVisible: false })}>{t('close')}</Button>
              <Button
                onClick={this.handleAddFromInventorySubmit}
                loading={this.state.addFromInventoryLoading}
                type="primary"
                disabled={isEmpty(this.state.addFromInventoryItems)}
              >
                {t('addToCycleCount')}
              </Button>
            </>
          }
          width={992}
        >
          <SelectInventory
            initialFilterDto={{
              locationIds: [this.state.item.locationId],
              unlockedInventoryOnly: true,
            }}
            onSelect={(values) => this.setState({ addFromInventoryItems: values })}
            mode="multiple"
            filterProps={{ disabled: true }}
          />
        </Modal>
        <Modal
          title={`${t('addFromList')} - ${item.locationName}`}
          visible={this.state.addFromListVisible}
          onCancel={() => this.setState({ addFromListVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ addFromListVisible: false })}>{t('close')}</Button>
              <Button
                onClick={this.handleAddFromListSubmit}
                loading={this.state.addFromListLoading}
                type="primary"
                disabled={isEmpty(this.state.addFromListItems)}
              >
                {t('select')}
              </Button>
            </>
          }
          width={992}
        >
          <SelectListItems
            onSelect={(values) => this.setState({ addFromListItems: values })}
            settingsType="cycleCountInventoryList"
            mode="multiple"
            filterProps={{
              locationId: item.locationId,
              entityType: 'Procurement',
              listType: 'Product',
              listItemMode: 'CycleCountInventoryList',
            }}
          />
        </Modal>
        <Drawer
          title={t('addFromFile')}
          size="xs"
          visible={this.state.addFromFileUploadVisible}
          onClose={() => this.setState({ addFromFileUploadVisible: false })}
        >
          <FileUpload
            accept=".csv,text/csv"
            onUpload={this.handleAddFromFileUpload}
            onCancel={() => this.setState({ addFromFileUploadVisible: false })}
            extra={{
              type: 'info',
              message: t('fileRequirements'),
              description: t('cycleCountUploadInfo'),
            }}
          />
        </Drawer>
        <Modal
          title={this.state.selectPopulatedItemsTitle}
          visible={this.state.selectPopulatedItemsVisible}
          onCancel={() => this.setState({ selectPopulatedItemsVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ selectPopulatedItemsVisible: false })}>{t('close')}</Button>
              <Button
                onClick={this.handleSelectPopulatedItemsSubmit}
                type="primary"
                disabled={isEmpty(this.state.selectPopulatedItems)}
              >
                {t('addToCycleCount')}
              </Button>
            </>
          }
          width={992}
        >
          {isNil(this.state.selectPopulatedItemsData) ? (
            <Spin />
          ) : (
            <SelectItems
              items={this.state.selectPopulatedItemsData ?? []}
              onSelect={(values) => this.setState({ selectPopulatedItems: values })}
              settingsType="cycleCountFileItemList"
              mode="multiple"
            />
          )}
        </Modal>
        <Modal
          title={t('printPreview')}
          okText={t('print')}
          onOk={createPrintHandler(this.printableRef)}
          onCancel={() => this.setState({ printVisible: false }, resetPrintHandler)}
          width={992}
          visible={this.state.printVisible}
        >
          <div ref={this.printableRef} className="tofino-print-preview">
            <Print
              parentRecord={{
                ...this.state.printItem,
                detailItems,
              }}
            />
          </div>
        </Modal>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
